import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, RefreshCw, Phone, Mail, Trash2, ImagePlus, Star } from "lucide-react";
import { PROJECT_CATEGORIES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (path) => `${API}/files/${path}`;

const inputCls =
  "w-full rounded-xl border border-brand-light bg-white px-4 py-3 text-sm text-brand-ink outline-none transition-all focus:border-brand-green";

const ReviewsTab = ({ adminKey }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", text: "" });
  const [rating, setRating] = useState(0);
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios.get(`${API}/reviews`).then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (rating) fd.append("rating", String(rating));
      if (img) fd.append("image", img);
      await axios.post(`${API}/reviews`, fd, { headers: { "x-admin-key": adminKey } });
      toast.success("Review published to the homepage.");
      setForm({ name: "", location: "", text: "" });
      setRating(0);
      setImg(null);
      e.target.reset();
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || "Could not publish — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers: { "x-admin-key": adminKey } });
      toast.success("Review removed.");
      load();
    } catch {
      toast.error("Could not remove — please try again.");
    }
  };

  return (
    <div className="mt-8 grid gap-10 lg:grid-cols-2">
      <form data-testid="review-add-form" onSubmit={submit} className="rounded-2xl border border-brand-light bg-white p-7 shadow-sm">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-brand-green">
          <Star className="h-5 w-5" /> Add a customer review
        </h2>
        <p className="mt-1 text-xs text-brand-muted">
          Only publish reviews a real customer has genuinely given you — with their permission. The homepage section appears automatically once the first review is added.
        </p>
        <div className="mt-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Customer first name *</label>
              <input required minLength={2} data-testid="review-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya" className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Area</label>
              <input data-testid="review-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Harrow" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Their words *</label>
            <textarea required minLength={10} rows={4} data-testid="review-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Paste exactly what the customer said…" className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Star rating (optional)</label>
            <div className="flex items-center gap-1" data-testid="review-rating-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  data-testid={`review-star-${n}`}
                  onClick={() => setRating(rating === n ? 0 : n)}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  className="rounded-lg p-1 transition-transform hover:scale-110 active:scale-95"
                >
                  <Star className={`h-7 w-7 transition-colors ${n <= rating ? "fill-amber-400 text-amber-400" : "text-brand-light hover:text-amber-300"}`} />
                </button>
              ))}
              {rating > 0 && <span className="ml-2 text-sm font-bold text-brand-muted">{rating}/5</span>}
            </div>
            <p className="mt-1.5 text-xs text-brand-muted">Only add stars if the customer actually gave a rating. Tap again to clear.</p>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Photo of the finished job (optional)</label>
            <input type="file" accept="image/*" data-testid="review-photo-input" onChange={(e) => setImg(e.target.files[0] || null)} className="w-full text-sm text-brand-muted file:mr-4 file:rounded-full file:border-0 file:bg-brand-light file:px-5 file:py-2.5 file:text-sm file:font-bold file:text-brand-green" />
            <p className="mt-1.5 text-xs text-brand-muted">Showing the actual work next to the words builds strong trust.</p>
          </div>
          <button type="submit" disabled={busy} data-testid="review-add-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-red py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-redDark disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? "Publishing…" : "Publish review"}
          </button>
        </div>
      </form>
      <div>
        <h2 className="font-heading text-xl font-bold text-brand-green">Published reviews</h2>
        <div className="mt-4 space-y-3" data-testid="review-list">
          {!items ? (
            <p className="text-sm text-brand-muted">Loading…</p>
          ) : items.length === 0 ? (
            <p data-testid="review-list-empty" className="rounded-2xl bg-brand-light/60 p-6 text-sm text-brand-muted">
              No reviews yet. Add your first genuine customer review and it will appear on the homepage.
            </p>
          ) : (
            items.map((r) => (
              <div key={r.id} data-testid={`review-item-${r.id.slice(0, 8)}`} className="flex items-start gap-4 rounded-2xl border border-brand-light bg-white p-5">
                {r.image_path && (
                  <img src={fileUrl(r.image_path)} alt={`Job photo for ${r.name}'s review`} className="h-14 w-16 shrink-0 rounded-xl object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-sm font-bold text-brand-green">
                    {r.name}{r.location ? ` · ${r.location}` : ""}
                    {r.rating ? <span className="ml-2 text-amber-500">{"★".repeat(r.rating)}<span className="text-brand-light">{"★".repeat(5 - r.rating)}</span></span> : null}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-brand-ink/75">“{r.text}”</p>
                </div>
                <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id.slice(0, 8)}`} aria-label={`Remove review from ${r.name}`} className="rounded-full p-2.5 text-brand-red transition-colors hover:bg-brand-red/10">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const PhotosTab = ({ adminKey }) => {  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ title: "", category: "Loft Conversions", location: "", desc: "" });
  const [img, setImg] = useState(null);
  const [before, setBefore] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios.get(`${API}/projects`).then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!img) {
      toast.error("Please choose a project photo.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("image", img);
      if (before) fd.append("before_image", before);
      await axios.post(`${API}/projects`, fd, { headers: { "x-admin-key": adminKey } });
      toast.success("Project photo published to the gallery.");
      setForm({ title: "", category: "Loft Conversions", location: "", desc: "" });
      setImg(null);
      setBefore(null);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: { "x-admin-key": adminKey } });
      toast.success("Project removed from the gallery.");
      load();
    } catch {
      toast.error("Could not remove — please try again.");
    }
  };

  return (
    <div className="mt-8 grid gap-10 lg:grid-cols-2">
      <form data-testid="photo-upload-form" onSubmit={submit} className="rounded-2xl border border-brand-light bg-white p-7 shadow-sm">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-brand-green">
          <ImagePlus className="h-5 w-5" /> Add a project photo
        </h2>
        <p className="mt-1 text-xs text-brand-muted">
          Uploaded photos appear on the public Projects page instantly and replace the placeholder imagery.
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Project title *</label>
            <input required data-testid="photo-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Dormer loft conversion" className={inputCls} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Category *</label>
              <select data-testid="photo-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                {PROJECT_CATEGORIES.filter((c) => c !== "All").map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Location</label>
              <input data-testid="photo-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Harrow" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Short description</label>
            <textarea rows={2} data-testid="photo-desc-input" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="What was done on this project…" className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Finished photo (after) *</label>
            <input required type="file" accept="image/*" data-testid="photo-file-input" onChange={(e) => setImg(e.target.files[0] || null)} className="w-full text-sm text-brand-muted file:mr-4 file:rounded-full file:border-0 file:bg-brand-light file:px-5 file:py-2.5 file:text-sm file:font-bold file:text-brand-green" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-green">Before photo (optional — powers the before/after slider)</label>
            <input type="file" accept="image/*" data-testid="photo-before-input" onChange={(e) => setBefore(e.target.files[0] || null)} className="w-full text-sm text-brand-muted file:mr-4 file:rounded-full file:border-0 file:bg-brand-light file:px-5 file:py-2.5 file:text-sm file:font-bold file:text-brand-green" />
          </div>
          <button type="submit" disabled={busy} data-testid="photo-upload-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-red py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-redDark disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? "Uploading…" : "Publish to gallery"}
          </button>
        </div>
      </form>

      <div>
        <h2 className="font-heading text-xl font-bold text-brand-green">Published photos</h2>
        <div className="mt-4 space-y-3" data-testid="photo-list">
          {!items ? (
            <p className="text-sm text-brand-muted">Loading…</p>
          ) : items.length === 0 ? (
            <p data-testid="photo-list-empty" className="rounded-2xl bg-brand-light/60 p-6 text-sm text-brand-muted">
              No genuine photos yet. Upload your first project photo and it will replace the placeholders on the site.
            </p>
          ) : (
            items.map((p) => (
              <div key={p.id} data-testid={`photo-item-${p.id.slice(0, 8)}`} className="flex items-center gap-4 rounded-2xl border border-brand-light bg-white p-3">
                <img src={fileUrl(p.image_path)} alt={p.title} className="h-16 w-20 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-sm font-bold text-brand-green">{p.title}</p>
                  <p className="text-xs text-brand-muted">{p.category}{p.location ? ` · ${p.location}` : ""}{p.before_path ? " · has before photo" : ""}</p>
                </div>
                <button onClick={() => remove(p.id)} data-testid={`photo-delete-${p.id.slice(0, 8)}`} aria-label={`Remove ${p.title}`} className="rounded-full p-2.5 text-brand-red transition-colors hover:bg-brand-red/10">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("kic_admin_key") || "");
  const [input, setInput] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    document.title = "Enquiries | KAC Admin";
  }, []);

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: { "x-admin-key": k } });
      setRows(data);
      setKey(k);
      localStorage.setItem("kic_admin_key", k);
    } catch {
      setError("Incorrect access key, or the server is unavailable.");
      setRows(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!rows) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center px-4 py-20">
        <form
          data-testid="admin-login-form"
          onSubmit={(e) => { e.preventDefault(); load(input); }}
          className="w-full max-w-sm rounded-2xl border border-brand-light bg-white p-8 shadow-lg"
        >
          <Lock className="h-8 w-8 text-brand-green" />
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-brand-green">KAC Admin</h1>
          <p className="mt-1 text-sm text-brand-muted">Enter your admin access key to view enquiries and manage project photos.</p>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="admin-key-input"
            placeholder="Access key"
            className="mt-5 w-full rounded-xl border border-brand-light px-4 py-3 text-sm outline-none focus:border-brand-green"
          />
          {error && <p data-testid="admin-error" className="mt-2 text-xs font-semibold text-brand-red">{error}</p>}
          <button type="submit" data-testid="admin-login-btn" disabled={loading} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand-red py-3 text-sm font-bold text-white transition-colors hover:bg-brand-redDark disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 data-testid="admin-title" className="font-heading text-3xl font-extrabold text-brand-green">KAC Admin</h1>
          <p className="mt-1 text-sm text-brand-muted">Manage quote enquiries and gallery photos</p>
        </div>
        <div className="flex gap-2" data-testid="admin-tabs">
          {[["enquiries", `Enquiries (${rows.length})`], ["photos", "Project Photos"], ["reviews", "Reviews"]].map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                tab === t ? "bg-brand-green text-white" : "bg-brand-light text-brand-green hover:bg-brand-green/15"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "photos" && <PhotosTab adminKey={key} />}
      {tab === "reviews" && <ReviewsTab adminKey={key} />}

      {tab === "enquiries" && (
        <>
          <div className="mt-6 flex justify-end">
            <button onClick={() => load(key)} data-testid="admin-refresh-btn" className="flex items-center gap-2 rounded-full border border-brand-green px-4 py-2 text-sm font-bold text-brand-green transition-colors hover:bg-brand-green hover:text-white">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
          <div className="mt-4 space-y-4">
            {rows.length === 0 && <p data-testid="admin-empty" className="rounded-2xl bg-brand-light/60 p-8 text-center text-brand-muted">No enquiries yet.</p>}
            {rows.map((r) => (
              <article key={r.id} data-testid={`admin-enquiry-${r.id.slice(0, 8)}`} className="rounded-2xl border border-brand-light bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-heading text-lg font-bold text-brand-green">{r.full_name}</h2>
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand-red">{r.service}</p>
                  </div>
                  <span className="rounded-full bg-brand-light px-3 py-1 text-xs font-bold text-brand-green">
                    {new Date(r.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 font-semibold text-brand-green"><Phone className="h-4 w-4" /> {r.phone}</a>
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 font-semibold text-brand-green"><Mail className="h-4 w-4" /> {r.email}</a>
                  <span className="text-brand-muted">{r.postcode}</span>
                  {r.budget && <span className="text-brand-muted">Budget: {r.budget}</span>}
                </div>
                <p className="mt-3 rounded-xl bg-brand-light/50 p-4 text-sm leading-relaxed text-brand-ink/80">{r.description}</p>
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
